import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext, lazy, Suspense } from 'react';
import { isNative } from './lib/capacitor';
import { initPushNotifications } from './lib/push';
import { API_BASE } from './lib/config';
import Layout from './components/Layout';
import { useToast } from './components/Toast';
import { setGlobalToast } from './main';
import { trpc } from './lib/trpc';

// Eager: landing page (always needed first)
import Landing from './pages/Landing';

// Lazy: everything else splits into separate chunks
const Auth = lazy(() => import('./pages/Auth'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Rooms = lazy(() => import('./pages/Rooms'));
const NewRoom = lazy(() => import('./pages/NewRoom'));
const Explore = lazy(() => import('./pages/Explore'));
const PersonaDetail = lazy(() => import('./pages/PersonaDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const GuestChat = lazy(() => import('./pages/GuestChat'));
const Settings = lazy(() => import('./pages/Settings'));
const Welcome = lazy(() => import('./pages/Welcome'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Admin = lazy(() => import('./pages/Admin'));
const SharedConversation = lazy(() => import('./pages/SharedConversation'));
const SharedEpisode = lazy(() => import('./pages/SharedEpisode'));
const Episodes = lazy(() => import('./pages/Episodes'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-pulse text-sage-400">Loading...</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <h1 className="text-2xl font-bold text-gray-100 mb-2">Page not found</h1>
      <p className="text-sage-400 mb-4">The page you're looking for doesn't exist.</p>
      <a href="/explore" className="text-gold hover:text-gold-300 transition-colors font-medium">
        Go to Explore
      </a>
    </div>
  );
}

type User = { id: string; email: string; name: string; profile: Record<string, unknown> | null };

type AuthCtx = {
  user: User | null;
  setUser: (u: User | null) => void;
  isLoading: boolean;
};

const AuthContext = createContext<AuthCtx>({ user: null, setUser: () => {}, isLoading: true });
export const useAuth = () => useContext(AuthContext);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-dvh">
        <div className="animate-pulse text-sage-400">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

/** Redirect authenticated users away from landing → rooms */
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-dvh">
        <div className="animate-pulse text-sage-400">Loading...</div>
      </div>
    );
  }
  if (user) return <Navigate to="/rooms" replace />;
  return <>{children}</>;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Bridge React toast context to global ref for MutationCache
  useEffect(() => {
    setGlobalToast(toast);
    return () => setGlobalToast(null);
  }, [toast]);

  const meQuery = trpc.user.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (meQuery.isSuccess) {
      setUser(meQuery.data);
      setIsLoading(false);
    } else if (meQuery.isError) {
      setUser(null);
      setIsLoading(false);
    }
  }, [meQuery.isSuccess, meQuery.isError, meQuery.data]);

  // Register for push notifications on native platforms after login
  useEffect(() => {
    if (user) {
      initPushNotifications(user.id, API_BASE);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading }}>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/" element={<PublicOnlyRoute><Landing /></PublicOnlyRoute>} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {!isNative && <Route path="/pricing" element={<Pricing />} />}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Public browsing — Explore & PersonaDetail work without auth */}
          <Route path="/explore" element={<Layout><Explore /></Layout>} />
          <Route path="/explore/:slug" element={<Layout><PersonaDetail /></Layout>} />

          {/* Guest trial chat — no auth, limited messages */}
          <Route path="/try/:slug" element={<GuestChat />} />

          {/* Shared conversation — public read-only view */}
          <Route path="/shared/:token" element={<SharedConversation />} />

          {/* Shared episode — public read-only view */}
          <Route path="/studio/shared/:token" element={<SharedEpisode />} />

          {/* Protected routes — require auth */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      <Route path="/rooms" element={<Rooms />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/welcome" element={<Welcome />} />
                      <Route path="/rooms/new" element={<NewRoom />} />
                      <Route path="/rooms/:id" element={<Chat />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/studio" element={<Episodes />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </AuthContext.Provider>
  );
}
